/**
 * Главная панель AI ассистента
 */

import { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  X,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ChatMessage } from './chat-message';
import { ChatInput } from './chat-input';
import { ChatHistory } from './chat-history';
import { ErrorDetailsPanel } from './error-details-panel';
import { useAiChat } from './use-ai-chat';
import { usePageContext } from './use-page-context';
import type { PageContext } from './types';

interface AiAssistantPanelProps {
  /** Внешний контроль открытия панели */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Экран приветствия для нового чата - минималистичный
 */
function WelcomeScreen({ onSuggestionClick }: { onSuggestionClick: (text: string) => void }) {
  const suggestions = [
    'Статистика за месяц',
    'Активные подписки',
    'Популярные курсы',
  ];

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8">
      <Sparkles className="h-10 w-10 text-primary/60 mb-4" />
      <p className="text-sm text-muted-foreground text-center mb-6">
        Чем могу помочь?
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {suggestions.map((text, i) => (
          <button
            key={i}
            type="button"
            className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors"
            onClick={() => onSuggestionClick(text)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Typing индикатор - упрощённый
 */
function TypingIndicator() {
  return (
    <div className="px-4 py-2">
      <div className="inline-flex items-center gap-1.5 px-3 py-2 bg-muted rounded-xl">
        <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

/**
 * Заголовок панели - минималистичный
 */
function PanelHeader({
  showHistory,
  setShowHistory,
  onClose,
}: {
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
  onClose: () => void;
}) {
  return (
    <div className="flex h-16 shrink-0 items-center justify-between px-4 border-b border-border bg-card">
      <span className="text-sm font-medium">
        {showHistory ? 'История' : 'AI Ассистент'}
      </span>

      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setShowHistory(!showHistory)}
          title={showHistory ? 'Назад к чату' : 'История чатов'}
        >
          <MessageSquare className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Основной контент панели
 */
function PanelContent({
  showHistory,
  setShowHistory,
  pageContext,
}: {
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
  pageContext: PageContext;
}) {
  const {
    state,
    chats,
    messages,

    selectedModel,
    setSelectedModel,

    // Router Agent
    routerEnabled,
    setRouterEnabled,
    routerModel,
    setRouterModel,

    // Web Search
    webSearchEnabled,
    setWebSearchEnabled,

    // Knowledge Base
    selectedDocuments,
    setSelectedDocuments,

    // Контекст
    totalTokensUsed,
    contextLimit,
    contextUsagePercent,

    createChat,
    selectChat,
    deleteChat,
    clearContext,

    sendMessage,
    regenerateLastMessage,
    deleteMessage,
    editMessage,
    switchBranch,

    isLoadingChats,
    isLoadingMessages,
    isSending,
  } = useAiChat();

  const scrollRef = useRef<HTMLDivElement>(null);

  // Автоскролл при новых сообщениях
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  const handleSendMessage = (text: string, attachments?: import('./types').Attachment[]) => {
    sendMessage(text, pageContext, attachments);
  };

  const handleNewChat = async () => {
    await createChat();
    setShowHistory(false);
  };

  // Показываем историю
  if (showHistory) {
    return (
      <ChatHistory
        chats={chats}
        currentChatId={state.currentChatId}
        isLoading={isLoadingChats}
        onSelectChat={id => {
          selectChat(id);
          setShowHistory(false);
        }}
        onDeleteChat={deleteChat}
        onNewChat={handleNewChat}
      />
    );
  }

  const hasMessages = messages.length > 0;

  // Находим индекс последнего сообщения ассистента
  const lastAssistantIndex = messages.reduce((lastIdx, msg, idx) =>
    msg.role === 'assistant' ? idx : lastIdx, -1);

  const handleRegenerate = () => {
    regenerateLastMessage(pageContext);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Сообщения - занимает всё доступное пространство */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea className="h-full" ref={scrollRef}>
          {!hasMessages && !isLoadingMessages ? (
            <WelcomeScreen onSuggestionClick={handleSendMessage} />
          ) : (
            <div className="py-4">
              {messages.map((message, index) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  isLastAssistantMessage={index === lastAssistantIndex}
                  onRegenerate={handleRegenerate}
                  isSending={isSending}
                  onDelete={deleteMessage}
                  onEdit={(messageId, newContent) => editMessage(messageId, newContent, pageContext)}
                  onSwitchBranch={switchBranch}
                />
              ))}

              {isSending && <TypingIndicator />}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Нижняя часть - фиксированная, sticky к низу */}
      <div className="flex-shrink-0">
        {/* Ошибка */}
        {state.error && (
          <ErrorDetailsPanel
            error={state.error}
            details={state.errorDetails}
          />
        )}

        {/* Ввод */}
        <ChatInput
          onSend={handleSendMessage}
          isLoading={isSending}
          pageContext={pageContext}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          routerEnabled={routerEnabled}
          onRouterEnabledChange={setRouterEnabled}
          routerModel={routerModel}
          onRouterModelChange={setRouterModel}
          webSearchEnabled={webSearchEnabled}
          onWebSearchEnabledChange={setWebSearchEnabled}
          selectedDocuments={selectedDocuments}
          onSelectedDocumentsChange={setSelectedDocuments}
          contextUsagePercent={contextUsagePercent}
          totalTokensUsed={totalTokensUsed}
          contextLimit={contextLimit}
          onClearContext={clearContext}
        />
      </div>
    </div>
  );
}

/**
 * AI Assistant Panel - Sheet на мобильных, боковая панель на десктопе
 */
export function AiAssistantPanel({
  isOpen = false,
  onOpenChange,
}: AiAssistantPanelProps) {
  const [showHistory, setShowHistory] = useState(false);
  const pageContext = usePageContext();

  const {
    state,
    openPanel,
    closePanel,
  } = useAiChat();

  // Синхронизация с внешним состоянием
  useEffect(() => {
    if (isOpen && !state.isOpen) {
      openPanel();
    } else if (!isOpen && state.isOpen) {
      closePanel();
    }
  }, [isOpen, state.isOpen, openPanel, closePanel]);

  const handleClose = () => {
    closePanel();
    onOpenChange?.(false);
  };

  return (
    <>
      {/* Mobile: Sheet */}
      <Sheet open={state.isOpen} onOpenChange={setIsOpen => setIsOpen || handleClose()} modal={false}>
        <SheetContent
          side="right"
          hideOverlay
          hideCloseButton
          title="AI Ассистент"
          className="p-0 w-full sm:max-w-md flex flex-col lg:hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <PanelHeader
            showHistory={showHistory}
            setShowHistory={setShowHistory}
            onClose={handleClose}
          />
          <PanelContent
            showHistory={showHistory}
            setShowHistory={setShowHistory}
            pageContext={pageContext}
          />
        </SheetContent>
      </Sheet>

      {/* Desktop: Fixed panel справа */}
      <div
        className={cn(
          'hidden lg:flex fixed top-0 right-0 bottom-0 z-40',
          'w-[400px] flex-col bg-background border-l',
          'transition-all duration-300',
          state.isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        )}
      >
        <PanelHeader
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          onClose={handleClose}
        />
        <PanelContent
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          pageContext={pageContext}
        />
      </div>
    </>
  );
}
